// What form are we editing?
import { chainOrSuspend, pyCall, pyStr, suspensionToPromise } from "@Sk";
import {
    Component,
    CreatingObject,
    DropZone,
    DroppingSpecification,
    PastingObjects,
    ToolboxItem,
    notifyComponentMounted,
    notifyComponentUnmounted,
} from "@runtime/components/Component";
import { resolveFormSpec } from "@runtime/runner/instantiation";
import { anvilMod, s_remove_from_parent } from "@runtime/runner/py-util";
import { DropModeFlags, Slot } from "@runtime/runner/python-objects";
import { ensureEditingFormIsNotCached } from "./app-content";
import { SlotPlaceholder } from "./components/SlotPlaceholder";
import { maxDistanceBetweenRects, unionDomRects } from "./geometry";
import { onceAtATime } from "./helpers";
import { inflateSelectedObjectDTO, notifySelectionChange, validateSelection } from "./index";
import { getNamedFormInstantiator } from "./instantiation";
import {
    Breadcrumb,
    DropZoneDTO,
    EditFormData,
    FormDidntInstantiateError,
    IdentifySelectedObjectDTO,
} from "./shared-types";
import * as windowRpc from "./windowRpc";

export type PathElement = string | number;
export type EditPath = PathElement[];

interface ComponentWithName {
    pyComponent: Component;
    name: string;
}

interface EditingForm {
    className: string;
    pyComponent: Component;
}

export type ComponentDropTargetType = "component" | "slot" | "slotTemplate";

export interface ComponentDropTarget {
    type: ComponentDropTargetType;
    name: string;
    getBounds(): DOMRect;
}

export interface AugmentedDropZone extends DropZone {
    id: number;
    dropTarget: ComponentDropTarget;
    originalBounds: DOMRect;
    floatingAncestors?: HTMLElement[];
    occludingElements?: HTMLElement[];
}

// For layout slots, a custom property in the designer only - use ts declaration-merging
declare module "../../../../runtime/client/js/runner/python-objects" {
    interface SlotState {
        templateComponent?: Component;
    }
}

// Represents a slot *on the active form*
export interface SlotInfo {
    slot: Slot;
    name: string;
    placeholder: SlotPlaceholder;
    template?: Component;
    element: HTMLElement;
    // id: undefined;
}

interface SlotTemplateInfo {
    targetType: "slot";
    slotName: string;
    slot: Slot;
    template: ToolboxItem;
}

interface ContainerInfo {
    index: number;
    layoutProperties: { [prop: string]: any };
    targetSlot?: string;
}

export type TemplateInfo = SlotTemplateInfo /* | ContainerTemplateInfo etc */;

export interface SectionInfo {
    name: string;
    id: string;
}

export interface DesignInteractionInfo {
    component: Component;
    callbacks: { [eventName: string]: (e: MouseEvent) => void };
}

export interface SelectedObjectData {}
export interface SelectedComponentData extends SelectedObjectData {
    name: string;
}

export interface SelectedSectionData extends SelectedComponentData {
    id: string;
}

export interface SelectedSlot {
    type: "slot";
    data: SlotInfo;
}

export interface SelectedSection {
    type: "section";
    data: SelectedSectionData;
}

export interface SelectedComponent {
    type: "component";
    data: SelectedComponentData;
}

export type SelectedObject = SelectedComponent | SelectedSlot | SelectedSection;

export interface DraggingObject {
    object: IdentifySelectedObjectDTO & { type: "component" | "slot" };
    element: HTMLElement;
    dummyElement: HTMLElement; // The cloned element we drag around under the mouse

    // Element state when we grabbed
    originalState: {
        position: DOMRect;
        opacity: string;
    };
}

interface DragState {
    objects: DraggingObject[];

    // Mouse position when we grabbed
    grabMouseX: number;
    grabMouseY: number;
    grabTime: number;
}

// editState is rebuilt from scratch every time we switch editingForm
interface EditState {
    topLevelFormName: string;
    editPath: EditPath;
    editingForm?: EditingForm; // This will be filled in when the editingForm is created.
    breadcrumb: Breadcrumb;
    componentsByName: Map<string, Component>;
    componentsByDomNode: WeakMap<Element, ComponentWithName>;
    componentTypes: WeakMap<Component, string>;
    templatesByDomNode: WeakMap<Element, TemplateInfo>;
    slotsByName: Map<string, SlotInfo>;
    slotsByDomNode: WeakMap<Element, SlotInfo>;
    slotsByComponent: Map<Component, SlotInfo>;
    domNodesBySection: Map<string, Map<string, Element>>;
    sectionsByDomNode: WeakMap<Element, SectionInfo>;
    namesByComponent: Map<Component, string>;
    ancestorsByComponent: Map<Component, Component[]>;
    yamlCarrierByDomNode: Map<Element, Component>;
    orphanedComponents: string[];
    orphanedSlots: string[];
    inlineEditing?: HTMLElement;

    layoutPropertyInfoByComponent: Map<Component, ContainerInfo>;

    designInteractionsByDomNode: WeakMap<Element, DesignInteractionInfo>;

    // Component creation
    dropZones: Map<HTMLElement, AugmentedDropZone>;
    dropZonesByComponent: Map<Component, AugmentedDropZone[]>;
    dropZonesBySlot: Map<Slot, AugmentedDropZone[]>;
    nearestDropZone: null | AugmentedDropZone;

    dragState: DragState | null;
}

// persistentEditState survives refreshing the current editingForm
interface PersistentEditState {
    selection: SelectedObject[];
    creating: null | CreatingObject;
    pasting: null | PastingObjects;
    designerState: Map<string, Map<string, any>>;
}

export let editState: EditState;
export let persistentEditState: PersistentEditState;

export function onInstantiatedEditingForm(className: string, pyComponent: Component) {
    editState.editingForm = { className, pyComponent };
    ensureEditingFormIsNotCached(className);
}

export const sendEditFormBounds = () =>
    windowRpc.raiseEvent(
        "editFormResized",
        editState?.editingForm?.pyComponent?.anvil$hooks.domElement?.getBoundingClientRect()
    );
const editFormObserver = new ResizeObserver(sendEditFormBounds);

const withEditFormLock = onceAtATime("editForm");

let topLevelComponent: undefined | Component;

export const startEditingForm = (
    topLevelFormName: string,
    editPath: EditPath,
    resetPersistentState: boolean,
    initialSelection?: IdentifySelectedObjectDTO[]
): Promise<EditFormData> =>
    withEditFormLock(async () => {
        // do this before we clear the dom
        const persistedScroll = resetPersistentState ? [] : serializeScrolledElements();
        try {
            // This exists because components (e.g. anvil-react components) might do cleanup in x-anvil-page-removed
            await suspensionToPromise(() => topLevelComponent && notifyComponentUnmounted(topLevelComponent, true));
        } finally {
            topLevelComponent = undefined;
        }
        editFormObserver.disconnect();
        editState = {
            topLevelFormName,
            editPath,
            breadcrumb: [],
            componentsByName: new Map(),
            orphanedComponents: [],
            orphanedSlots: [],
            componentsByDomNode: new WeakMap(),
            componentTypes: new WeakMap(),
            templatesByDomNode: new WeakMap(),
            sectionsByDomNode: new WeakMap(),
            domNodesBySection: new Map(),
            slotsByName: new Map(),
            slotsByDomNode: new Map(),
            slotsByComponent: new Map(),
            namesByComponent: new Map(),
            ancestorsByComponent: new Map(),
            yamlCarrierByDomNode: new Map(),
            layoutPropertyInfoByComponent: new Map(),
            designInteractionsByDomNode: new WeakMap(),
            dropZones: new Map(),
            dropZonesByComponent: new Map(),
            dropZonesBySlot: new Map(),
            nearestDropZone: null,
            dragState: null,
        };
        if (resetPersistentState) {
            persistentEditState = {
                selection: [],
                creating: null,
                pasting: null,
                designerState: new Map(),
            };
        } else if (initialSelection) {
            // reset the selection
            // if initial selection is set - we set this after instantiating the form
            // slots need to be realized before we can get their selection data
            persistentEditState.selection = [];
        }

        // For debugging
        // @ts-ignore
        window.anvilEditState = editState;

        try {
            await Sk.misceval.asyncToPromise(() =>
                chainOrSuspend(
                    getNamedFormInstantiator(resolveFormSpec(topLevelFormName, null), undefined, { isTopLevel: true }),
                    (instantiate) => instantiate(),
                    (pyComponent) =>
                        chainOrSuspend(pyComponent.anvil$hooks.setupDom(), (domNode) => {
                            // Clear the placeholder and render the component
                            const placeHolder = document.getElementById("appGoesHere")!;
                            placeHolder.replaceChildren(domNode);
                            topLevelComponent = pyComponent;
                            return notifyComponentMounted(pyComponent, true);
                        })
                )
            );
        } catch (e) {
            const placeHolder = document.getElementById("appGoesHere")!;
            const message = `Form failed to load:\n\n${String(
                e
            )}\n\nSee "Designer Output" in the bottom panel for full stack trace`;
            const invalidComponent = pyCall(anvilMod.InvalidComponent, [], ["text", new pyStr(message)]);
            placeHolder.replaceChildren(invalidComponent.anvil$hooks.domElement);
            throw e;
        }

        if (!editState.editingForm) {
            // If editState.editingForm is undefined at this point, we have been asked to edit an edit path that
            // did not successfully instantiate itself (yet). Either we blew up internally, or (more likely)
            // something on the editPath did not instantiate as it was expected. (This is very user-triggerable,
            // by e.g. only instantiating an editable child form in a show event, or something.)
            // Put the designer into a clear state with a searchable error message, and ideally allow recovery.
            throw new FormDidntInstantiateError();
        }
        const editForm: EditFormData = {
            className: editState.editingForm.className,
            breadcrumb: editState.breadcrumb,
        };
        if (editPath.length > 0) {
            const domElement = editState.editingForm.pyComponent.anvil$hooks.domElement!;
            editForm.bounds = domElement.getBoundingClientRect();
            editFormObserver.observe(domElement);
        }

        if (initialSelection) {
            // explicitly given an initial selection - use it
            // e.g. renaming the selected component
            persistentEditState.selection = initialSelection.map(inflateSelectedObjectDTO);
        }
        // Validate persistentEditState - make sure that any selected components still exist
        validateSelection();
        notifySelectionChange();
        if (!resetPersistentState) {
            restoreScroll(persistedScroll);
        }

        // Now that we've rebuilt the form, extract dropzones if necessary
        updateDropMode();

        windowRpc.raiseEvent("notifyEditForm", editForm);
        windowRpc.raiseEvent("notifyOrphanedObjects", editState.orphanedComponents, editState.orphanedSlots);

        return editForm;
    });

export async function refreshEditingForm(initialSelection?: IdentifySelectedObjectDTO[]) {
    return await startEditingForm(editState.topLevelFormName, editState.editPath, false, initialSelection);
}

export function setupActiveYamlDomElement(pyComponent: Component, name: string) {
    const domElement = pyComponent.anvil$hooks.domElement!;
    if (!domElement) return;
    editState.componentsByDomNode.set(domElement, { name, pyComponent });
    domElement.setAttribute("data-anvil-selectable", "true");
    domElement.setAttribute("data-anvil-component-name", name);
    domElement.setAttribute("anvil-active-carrier-component", "");
}

export function updateComponentDomNode(pyComponent: Component) {
    const name = editState.namesByComponent.get(pyComponent);
    // Only if this component is in an active YAMLCarrier
    if (name !== undefined) {
        setupActiveYamlDomElement(pyComponent, name);
    }
}

const walkFloatingPanels = (element: HTMLElement): HTMLElement[] => {
    const styles = getComputedStyle(element);
    const floating =
        styles["position"] === "absolute" && styles["opacity"] !== "0" && styles["visibility"] === "visible";
    return [
        ...(floating ? [element] : []),
        ...(element.parentElement ? walkFloatingPanels(element.parentElement) : []),
    ];
};

let nextDropZoneId = 1;

// TODO: All uses of this function should probably await.
export async function updateDropMode() {
    if (!editState.editingForm) {
        throw new FormDidntInstantiateError();
    }
    const { creating, pasting } = persistentEditState;
    if (creating || pasting || editState.dragState) {
        editState.dropZones.clear();
        editState.dropZonesByComponent.clear();
        editState.dropZonesBySlot.clear();

        const dropping: DroppingSpecification = creating
            ? { creating }
            : pasting
            ? { pasting }
            : {
                  dragging: editState.dragState!.objects.map(({ object }) => ({
                      type: object.type,
                      name: object.data.name,
                  })),
              };

        const allFloatingElements: HTMLElement[] = [];
        const addDropZonesForComponent = (component: Component, name: string, flags: DropModeFlags) => {
            const dzs = component.anvil$hooks.enableDropMode?.(dropping, flags);
            if (dzs) {
                const augmentedDropZones: AugmentedDropZone[] = [];
                for (const dz of dzs) {
                    dz.element.setAttribute("anvil-dropzone", "true");
                    const floatingAncestors = walkFloatingPanels(dz.element);
                    allFloatingElements.push(...floatingAncestors);
                    const augmentedDropZone: AugmentedDropZone = {
                        ...dz,
                        id: nextDropZoneId++,
                        dropTarget: {
                            type: "component",
                            name,
                            getBounds: () => component.anvil$hooks.domElement!.getBoundingClientRect(),
                        },
                        originalBounds: dz.element.getBoundingClientRect(),
                        floatingAncestors,
                    };
                    editState.dropZones.set(dz.element, augmentedDropZone);
                    augmentedDropZones.push(augmentedDropZone);
                }
                editState.dropZonesByComponent.set(component, augmentedDropZones);
            }
        };
        // Find dropzones on root editingForm - handle this separately as "" in componentsByName could be the layout
        addDropZonesForComponent(editState.editingForm.pyComponent, "", {
            allowOtherComponentUpdates: true,
            asComponent: false,
        });

        // Find dropzones on all components.
        for (const [name, component] of editState.componentsByName.entries()) {
            if (name !== "") {
                // Root form handled separately - could be a layout
                addDropZonesForComponent(component, name, { allowOtherComponentUpdates: true, asComponent: true });
            }
        }

        // Find dropzones on all available slots
        // TODO: Limit by what we're about to drop
        for (const [
            pySlotName,
            pySlot,
        ] of editState.editingForm.pyComponent.anvil$yamlCarrier!.pyLayoutSlots?.$items() || []) {
            const slotState = pySlot._slotState;
            if (slotState.canAddComponent() || slotState.templateComponent) {
                let dropZones: DropZone[];
                if (slotState.templateComponent) {
                    const element = slotState.templateComponent.anvil$hooks.domElement!;
                    element.setAttribute("anvil-dropzone", "true");
                    dropZones = [{ element, dropInfo: {} }];
                } else {
                    dropZones = slotState.enableDropMode(dropping);
                }
                const augmentedDropZones: AugmentedDropZone[] = [];
                for (const { element, dropInfo } of dropZones) {
                    const floatingAncestors = walkFloatingPanels(element);
                    allFloatingElements.push(...floatingAncestors);
                    const augmentedDropZone: AugmentedDropZone = {
                        id: nextDropZoneId++,
                        element,
                        expandable: false,
                        dropInfo: dropInfo,
                        dropTarget: {
                            type: "slot",
                            name: pySlotName.toString(),
                            getBounds: () =>
                                unionDomRects(dropZones.map(({ element }) => element.getBoundingClientRect()))!,
                        },
                        originalBounds: element.getBoundingClientRect(),
                        floatingAncestors,
                    };
                    editState.dropZones.set(element, augmentedDropZone);
                    augmentedDropZones.push(augmentedDropZone);
                }
                editState.dropZonesBySlot.set(pySlot, augmentedDropZones);
            }
        }

        // Find dropzones on slots that we're editing - we can drop template components there
        // TODO: Limit by what we're about to drop
        // This is currently DISABLED because slot templates didn't turn out to be a particularly good idea
        /*
        if (dropping.creating?.type !== "slot" && !dropping.pasting?.reduce((acc, d) => acc || d.type === "slot", false) && !dropping.dragging?.reduce((acc, d) => acc || d.type === "slot", false)) {
            // Don't allow dropping slots in slots from this layout.
            for (const [name, slot] of editState.slotsByName.entries()) {
                if (!slot.template) {
                    // This slot doesn't have a template, so we can drop a component there.
                    const {name, element} = slot;
                    element.setAttribute("anvil-dropzone", "true");
                    editState.dropZones.set(element, {
                        element,
                        id: nextDropZoneId++,
                        expandable: false,
                        dropTarget: {type: 'slotTemplate', name, getBounds: () => element.getBoundingClientRect()},
                        originalBounds: element.getBoundingClientRect(),
                        dropInfo: {},
                    });
                }
            }
        }*/

        if (dropping.dragging) {
            const draggingElements = editState.dragState!.objects.map((obj) => obj.element);
            const draggingComponents = draggingElements
                .map((element) => editState.componentsByDomNode.get(element)?.pyComponent)
                .filter(Boolean);

            // Look through the components that have dropzones we found, removing any that are inside things we're dragging
            for (const [component, augmentedDropZone] of editState.dropZonesByComponent.entries()) {
                for (const draggingComponent of draggingComponents) {
                    if (!draggingComponent) continue;
                    if (
                        draggingComponent === component ||
                        editState.ancestorsByComponent.get(component)?.includes(draggingComponent)
                    ) {
                        for (const aDZ of augmentedDropZone) {
                            editState.dropZones.delete(aDZ.element);
                        }
                        break;
                    }
                }
            }

            // Look through all the dropzones we found, removing any that are inside things we're dragging
            for (const element of [...editState.dropZones.keys()]) {
                for (const draggingElement of draggingElements) {
                    if (draggingElement.contains(element)) {
                        editState.dropZones.delete(element);
                        break;
                    }
                }
            }
        }

        // Work out which floating elements occlude which dropzones
        for (const dz of editState.dropZones.values()) {
            dz.occludingElements = allFloatingElements.filter((e) => !dz.floatingAncestors?.includes(e));
        }

        // Look through all the dropzones we've found, and make any that are too close together expandable.
        // This could be made much more efficient if necessary.
        for (const dz1 of editState.dropZones.values()) {
            if (dz1.expandable) continue;
            if (dz1.originalBounds.width < 20 && dz1.originalBounds.height < 20) {
                dz1.expandable = true;
            }
            for (const dz2 of editState.dropZones.values()) {
                if (dz2 === dz1) continue;
                const dist = maxDistanceBetweenRects(dz1.originalBounds, dz2.originalBounds);
                if (dist < 20) {
                    dz1.expandable = dz2.expandable = true;
                }
            }
        }

        const dzsForIde: DropZoneDTO[] = [];
        for (const { id, dropTarget, dropInfo } of editState.dropZones.values()) {
            if (dropTarget.type !== "slotTemplate") {
                // TODO support these if it's ever worth it
                dzsForIde.push({
                    id,
                    target: { type: dropTarget.type, data: { name: dropTarget.name } },
                    minChildIdx: dropInfo?.minChildIdx,
                    maxChildIdx: dropInfo?.maxChildIdx,
                });
            }
        }

        windowRpc.raiseEvent("notifyDropZones", dzsForIde);
        // TODO: Trigger updateDropZones here, not just on mouseMove in event-interception
    } else {
        editState.dropZones.clear();
        editState.dropZonesByComponent.clear();
        editState.dropZonesBySlot.clear();

        editState.editingForm.pyComponent.anvil$hooks.disableDropMode?.();
        for (const c of editState.componentsByName.values()) {
            c.anvil$hooks.disableDropMode?.();
        }
        for (const [
            _pySlotName,
            pySlot,
        ] of editState.editingForm.pyComponent.anvil$yamlCarrier!.pyLayoutSlots?.$items() || []) {
            const slotState = pySlot._slotState;
            slotState.disableDropMode();
            if (slotState.lastUse?._SlotDropZone) {
                const pyRemoveFromParent = slotState.lastUse.tp$getattr(s_remove_from_parent)!;
                Sk.misceval.callsimArray(pyRemoveFromParent, []);
            }
        }

        windowRpc.raiseEvent("notifyDropZones", null);
        windowRpc.raiseEvent("updateDropZones", []);
    }
}

const getComponentSectionsForComponentName = (name: string) => {
    let componentSection = editState.domNodesBySection.get(name);
    if (componentSection === undefined) {
        componentSection = new Map();
        editState.domNodesBySection.set(name, componentSection);
    }
    return componentSection;
};

export const setDomNodeForSection = (name: string, id: string, element: Element) => {
    const componentSections = getComponentSectionsForComponentName(name);
    const oldElement = componentSections.get(id);
    if (element && oldElement === element) return;
    if (oldElement) {
        editState.sectionsByDomNode.delete(oldElement);
    }
    if (!element) {
        console.error(`No element provided for component '${name}' with section ${id}`);
        return;
    }

    componentSections.set(id, element);
    editState.sectionsByDomNode.set(element, { name, id });

    if (!element.getAttribute("data-anvil-selectable")) {
        element.setAttribute("data-anvil-selectable", "true");
    }
};

export const deleteSection = (name: string, id: string) => {
    const componentSections = getComponentSectionsForComponentName(name);
    const oldElement = componentSections.get(id);
    if (oldElement) {
        editState.sectionsByDomNode.delete(oldElement);
    }
    componentSections.delete(id);
};

export const reloadSections = (name: string, component: Component) => {
    const sections = component.anvil$hooks.getSections?.() || [];

    const componentSections = getComponentSectionsForComponentName(name);

    Array.from(componentSections?.keys() ?? []).forEach((id) => deleteSection(name, id));

    for (const { id, element } of sections) {
        const sectionInfo = { name, id };
        componentSections.set(id, element);
        editState.sectionsByDomNode.set(element, sectionInfo);
        element.setAttribute("data-anvil-selectable", "true");
    }
};

export const setupSections = reloadSections;

interface PersistedScroll {
    name: string;
    path: number[];
    scrollLeft: number;
    scrollTop: number;
    scrollWidth: number;
    scrollHeight: number;
}

function serializeScrollPath(element: HTMLElement) {
    const path = [];
    let currentElement = element;
    while (currentElement && !currentElement.hasAttribute("data-anvil-component-name")) {
        let parent = currentElement.parentElement;
        if (!parent) break;
        let index = Array.prototype.indexOf.call(parent.children, currentElement);
        path.unshift(index); // Store both index and total children
        currentElement = parent;
    }
    if (currentElement === document.documentElement) {
        return { path, name: "$DOCUMENT$" };
    }
    const component = editState.componentsByDomNode.get(currentElement);
    if (!component) return;
    return { path, name: component.name };
}

function restoreScrollFail() {
    console.log("Failed to restore scroll");
}

function restoreScroll(persistedScroll: PersistedScroll[]) {
    // console.log("%cRESTORING SCROLL", "color: red;", persistentEditState.scroll);

    for (const { name, path, scrollLeft, scrollTop, scrollWidth, scrollHeight } of persistedScroll) {
        let el: HTMLElement | undefined | null;
        if (name === "$DOCUMENT$") {
            el = document.documentElement;
        } else {
            const component = editState.componentsByName.get(name);
            if (!component) {
                return restoreScrollFail();
            }
            el = component.anvil$hooks.domElement;
        }
        if (!el) continue;
        let currentElement = el;
        for (const index of path) {
            if (index >= currentElement.children.length) {
                return restoreScrollFail();
            }
            currentElement = currentElement.children[index] as HTMLElement;
        }
        if (scrollTop) {
            currentElement.scrollTop = scrollHeight === scrollTop ? currentElement.scrollHeight : scrollTop;
        }
        if (scrollLeft) {
            currentElement.scrollLeft = scrollLeft === scrollWidth ? currentElement.scrollWidth : scrollLeft;
        }
    }
}

function serializeScrolledElements() {
    const persistedScroll: PersistedScroll[] = [];
    document.querySelectorAll<HTMLElement>("[data-designer-scroll]").forEach((target) => {
        const scrollData = target.dataset.designerScroll;
        if (!scrollData) return;
        const serialized = serializeScrollPath(target);
        if (!serialized) return;
        const { path, name } = serialized;
        const [scrollLeft, scrollTop, scrollWidth, scrollHeight] = scrollData.split(":").map(Number);
        persistedScroll.push({ name, path, scrollLeft, scrollTop, scrollWidth, scrollHeight });
    });
    return persistedScroll;
}

document.addEventListener(
    "scroll",
    (event) => {
        let target = event.target === document ? document.documentElement : (event.target as HTMLElement | null);
        // console.log(target);
        if (!target) return;
        if (target.scrollLeft === 0 && target.scrollTop === 0) {
            delete target.dataset.designerScroll;
        } else {
            target.dataset.designerScroll = `${target.scrollLeft}:${target.scrollTop}:${target.scrollWidth}:${target.scrollHeight}`;
        }
    },
    true
);
